import { formatPercent, formatUSD } from '@/lib/format-utils'
import { Badge } from '@/components/ui/badge'
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import Skeleton from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

const percent = (value) => formatPercent(value, 0, 1)
const money = (value) => formatUSD(value, 0, 0)
// MON-55: 三张预测卡下方的贡献毛利，英文、千分位、无小数（formatUSD 会去掉千分位）
const profitText = (value) => {
  if (!Number.isFinite(value)) return 'Profit -'
  const rounded = Math.round(value)
  return `Profit ${rounded < 0 ? '-' : ''}$${Math.abs(rounded).toLocaleString('en-US')}/day`
}

// 回本线 100%，±3 点为预测误差带，与业务群表同规则
const TONES = {
  red: 'border-destructive/30 text-destructive',
  yellow: 'border-warning/30 text-warning',
  green: 'border-success/30 text-success',
  muted: 'text-muted-foreground',
}
const d90Tone = (value) => {
  if (value == null) return null
  if (value < 0.97) return 'red'
  if (value <= 1.03) return 'yellow'
  return 'green'
}
// 花费周环比只展示不作判据：涨绿跌红，持平或缺字段（旧留档）不着色
const wowText = (value) => (value == null ? '-' : `${value > 0 ? '+' : ''}${(value * 100).toFixed(1)}%`)
const wowTone = (value) => (value == null || value === 0 ? 'muted' : value > 0 ? 'green' : 'red')
const gapText = (gap) => {
  if (gap == null) return '-'
  const points = gap * 100
  return points > 0 ? `差 ${points.toFixed(1)}` : `超 ${(-points).toFixed(1)}`
}

// 手机 2 张一行，768 宽 3 张，1440 宽 5 张。
const GRID = 'grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5'

/** dashboard-01 section-cards 的状态角标：outline 变体 + 语义色 */
function StatusBadge({ tone, children }) {
  return (
    <Badge variant="outline" className={cn('h-6 shrink-0 px-2 text-xs font-medium', TONES[tone])}>
      {children}
    </Badge>
  )
}

export function Kpi({ label, value, detail, className, muted, badge, children }) {
  return (
    <Card className={cn(muted && 'opacity-60')}>
      {/* CardHeader 自带 1.5 的子元素纵向间距，叠加 gap-0.5 共 0.5rem */}
      <CardHeader className="gap-0.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <CardDescription>{label}</CardDescription>
          {badge}
        </div>
        <CardTitle className={cn('text-xl font-semibold tabular-nums', className)}>{value}</CardTitle>
      </CardHeader>
      {(detail || children) && (
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          {detail && <div className="text-muted-foreground">{detail}</div>}
          {children}
        </CardFooter>
      )}
    </Card>
  )
}

/** KPI 卡：5 张各层通用。 */
export function KpiCards({ row }) {
  return (
    <div className={GRID}>
      <Kpi label="Last 28d avg cost" value={money(row.dailyCost)}>
        <StatusBadge tone={wowTone(row.costWow)}>WoW {wowText(row.costWow)}</StatusBadge>
      </Kpi>
      <Kpi
        label="预测 D90"
        value={percent(row.d90)}
        detail={profitText(row.profit90)}
        badge={row.d90 != null && <StatusBadge tone={d90Tone(row.d90)}>距回本线 {gapText(row.gapToPayback)}</StatusBadge>}
      />
      <Kpi
        label="预测 D180"
        className="flex items-baseline gap-1"
        value={
          <>
            {percent(row.d180)}
            {row.attainableD180 != null && (
              <span className="text-sm font-normal text-muted-foreground" title="目标线（可达线）D180">
                / {percent(row.attainableD180)}
              </span>
            )}
          </>
        }
        detail={profitText(row.profit180)}
      />
      <Kpi label="回本日" value={row.paybackDay == null ? '>180' : `D${row.paybackDay}`} />
      <Kpi muted label="LTV（保守）" value={percent(row.ltv)}>
        <div className="text-muted-foreground">{profitText(row.profitLtv)}</div>
        <div className="text-muted-foreground">样本覆盖 {percent(row.ltvCostShare)}</div>
      </Kpi>
    </div>
  )
}

/** 首屏加载：5 张 KPI 卡和表格占位。 */
export function KpiSkeleton() {
  return (
    <div role="status" className="flex flex-col gap-4 md:gap-6">
      <div className={GRID}>
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton key={index} className="h-36 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  )
}

/** 区块加载：表格占位 */
export function SectionSkeleton() {
  return <Skeleton role="status" className="h-64 rounded-xl" />
}
